"use client";

import Image from "next/image";
import Link from "next/link";

import {
    Search,
    Menu,
    ShoppingBag,
    User,
    LayoutDashboard,
    LogOut,
} from "lucide-react";

import { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";
import { useCartStore } from "@/store/cart.store";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { useAuth } from "@/features/auth/hook/useAuth";
import { SearchBar } from "@/components/layout/clientLayout/navbar/SearchBar";
import { CategoriesDropdown } from "@/features/categories/components/CategoriesDropdown";
import { useSettings } from "@/features/settings/hook/useSettings";

const NAV_LINKS = [
    { label: "Tous les produits", href: "/products" },
    { label: "Nouveaux arrivages", href: "/new-arrival" },
    { label: "Promotions", href: "/offre-special" },
    { label: "Pack Direct", href: "/nos-packs" },
];

const ADMIN_ROLES = ["ADMIN", "SUPERADMIN", "VENDOR"];


export default function Navbar() {
    const pathname = usePathname();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const profileTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reset search when navigating away from search page
    useEffect(() => {
        if (!pathname.includes("/search")) {
            setSearch("");
        }
    }, [pathname]);

    const cartItemsCount = useCartStore((s) => s.items.length);
    const openCart = useCartStore((s) => s.openCart);
    const user = useAuthStore((s) => s.user);
    const { logout } = useAuth();
    const { data: settings } = useSettings();
    const dashboardHref = ADMIN_ROLES.includes(user?.role ?? "")
        ? "/admin/dashboard"
        : "/dashboard";

    const handleMouseEnter = () => {
        if (profileTimeoutRef.current) clearTimeout(profileTimeoutRef.current);
        setIsProfileOpen(true);
    };

    const handleMouseLeave = () => {
        profileTimeoutRef.current = setTimeout(() => {
            setIsProfileOpen(false);
        }, 150);
    };

    return (
        <header className="w-full sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-primary-100">

            {/* TOP BAR */}
            <div className="container mx-auto px-4 h-14 md:h-20 flex items-center justify-between">

                {/* Logo */}
                <Link href="/">
                    <Image
                        src={settings?.logoUrl || "/assets/logo/logo.png"}
                        alt="Logo"
                        width={60}
                        height={60}
                        className="w-10 h-10 md:w-14 md:h-14 object-contain"
                    />
                </Link>

                {/* Desktop Search */}
                <div className="hidden md:flex flex-1 justify-center">
                    <SearchBar value={search} onChange={setSearch} />
                </div>

                {/* Right Actions */}
                <div className="flex items-center gap-4">

                    {/* Mobile Search */}
                    <button
                        className="md:hidden text-primary-800"
                        onClick={() => setIsSearchOpen(!isSearchOpen)}
                        aria-label="Rechercher"
                    >
                        <Search className="h-6 w-6" />
                    </button>

                    {/* Cart */}
                    <button
                        onClick={openCart}
                        className="hidden md:block relative text-primary-800"
                        aria-label="Ouvrir le panier"
                    >
                        <ShoppingBag className="h-7 w-7" />
                        {cartItemsCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-secondary-500 text-white text-[10px] font-bold h-4 w-4 rounded-full flex items-center justify-center">
                                {cartItemsCount}
                            </span>
                        )}
                    </button>

                    {/* Profile */}
                    {user ? (
                        // Logged in — avatar with hover dropdown
                        <div
                            className="relative hidden md:block"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            {/* Avatar trigger */}
                            <button className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-primary-300 transition-transform hover:scale-105 shadow-sm focus:outline-none">
                                {user?.profileImage ? (
                                    <Image
                                        src={user.profileImage}
                                        alt={user.name}
                                        fill
                                        className="object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-primary-50 flex items-center justify-center text-primary-300">
                                        <span className="text-2xl font-bold uppercase">{user?.name?.charAt(0)}</span>
                                    </div>
                                )}
                            </button>

                            {/* Dropdown */}
                            <div
                                className={cn(
                                    "absolute right-0 top-full mt-2 w-52 bg-white rounded-xl border border-slate-100 shadow-lg overflow-hidden",
                                    "transition-all duration-150 origin-top-right",
                                    isProfileOpen
                                        ? "opacity-100 scale-100 pointer-events-auto"
                                        : "opacity-0 scale-95 pointer-events-none"
                                )}
                            >
                                {/* User info header */}
                                <div className="px-4 py-3 border-b border-slate-100">
                                    <p className="text-sm font-semibold text-slate-800 truncate">
                                        {user.name}
                                    </p>
                                    <p className="text-xs text-slate-400 truncate mt-0.5">
                                        {user.email}
                                    </p>
                                </div>

                                {/* Menu items */}
                                <div className="py-1">
                                    <Link
                                        href={dashboardHref}
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                        onClick={() => setIsProfileOpen(false)}
                                    >
                                        <LayoutDashboard className="h-4 w-4 text-slate-400" />
                                        Mon tableau de bord
                                    </Link>

                                    <button
                                        onClick={() => {
                                            setIsProfileOpen(false);
                                            logout();
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Se déconnecter
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Not logged in — icon link to login
                        <Link
                            href="/auth/login"
                            className="hidden md:block text-primary-800"
                            aria-label="Se connecter"
                        >
                            <User className="h-7 w-7" />
                        </Link>
                    )}

                    {/* Mobile Menu */}
                    <button
                        className="md:hidden text-primary-800"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Ouvrir le menu"
                    >
                        <Menu className="h-7 w-7" />
                    </button>
                </div>
            </div>

            {/* Mobile Search */}
            {isSearchOpen && (
                <div className="md:hidden px-4 pb-4 bg-primary-50 border-t border-primary-100">
                    <SearchBar
                        mobile
                        value={search}
                        onChange={setSearch}
                        onSubmit={() => setIsSearchOpen(false)}
                    />
                </div>
            )}

            {/* Desktop Navigation */}
            <nav className="hidden md:block bg-primary-50 border-t border-primary-100">
                <div className="container mx-auto px-6 h-12 flex items-center">
                    <ul className="flex items-center gap-8">
                        <li>
                            <CategoriesDropdown />
                        </li>
                        {NAV_LINKS.map((link) => (
                            <li key={link.label}>
                                <Link
                                    href={link.href}
                                    className="text-sm font-medium text-primary-700 hover:text-primary-900 flex items-center gap-1"
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </nav>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden bg-white border-t border-primary-100 p-6 space-y-4">
                    {NAV_LINKS.map((link) => (
                        <Link
                            key={link.label}
                            href={link.href}
                            className="block text-lg font-medium text-primary-800"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {link.label}
                        </Link>
                    ))}


                </div>
            )}
        </header>
    );
}